import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import {
  TextareaWithConfirm,
  CheckboxOptionsWithConfirm,
  InputWithConfirm,
  SelectWithOtherWithConfirm,
  SubHeading
} from 'shared-components';
import { ICheckbox } from '../../../../types';
import { titleAppend } from '../../../../utilities/head';
import { ApplicationState } from '../../../../store';
import * as StudentActions from '../../../../store/student/actions';
import * as PCIActions from '../../../../store/student/preCasInterview/actions';
import { createNavigateForwardAction, createNavigateToAction } from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/preCasInterview/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../store/ui/actions';

const InterviewQPage1 = () => {
  const dispatch = useDispatch();

  const pageTitle = 'Pre-CAS Questionnaire';
  const currentStep = 1;
  const steps = 4;

  const onNext = () => {
    dispatch(StudentActions.savePreCasInterview(Pages.PAGE1));
    dispatch(createNavigateForwardAction());
  };
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(PCIActions.createUpdatePCIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  const {
    whyUk = '',
    whyUkOther = '',
    selectedCourseReasons = [],
    selectedCourseReasonsOther = '',
    selectedUniReasons = [],
    selectedUniReasonsOther = '',
    uniChoiceReason1 = '',
    uniChoiceReason2 = '',
    uniChoiceReason3 = ''
  } = useSelector((state: ApplicationState) => state.student.preCasInterview);
  const pageConfig = useSelector((state: ApplicationState) => state.config.Interview.Page1);

  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const { complete: isComplete } = getPageStatus({
      whyUk,
      whyUkOther,
      selectedCourseReasons,
      selectedCourseReasonsOther,
      selectedUniReasons,
      selectedUniReasonsOther,
      uniChoiceReason1,
      uniChoiceReason2,
      uniChoiceReason3
    });
    setComplete(isComplete);
  }, [
    whyUk,
    whyUkOther,
    selectedCourseReasons,
    selectedCourseReasonsOther,
    selectedUniReasons,
    selectedUniReasonsOther,
    uniChoiceReason1,
    uniChoiceReason2,
    uniChoiceReason3
  ]);

  const getWhyUkOptions = () => {
    const options = [
      'High quality of education',
      'Time alone to focus on study',
      'Experience in a new culture',
      'Positive impression after travel to the country',
      'Family ties to the country',
      'Recommendation (e.g. Friend or family)'
    ];

    return options.map(option => ({
      label: option,
      value: option
    }));
  };

  const getCourseReasons = (): ICheckbox[] => {
    const courseReasons = [
      'Aligned with my career goals',
      'Course content (modules)',
      'Recommendation (e.g. Friend or family)',
      'I am interested in this subject area',
      'The course follows on from my previous study',
      'Other',
      'I am not sure why I chose this course'
    ];

    return courseReasons.map((reason: string) => ({
      id: reason,
      label: reason,
      isTicked: !!(selectedCourseReasons && selectedCourseReasons.includes(reason)),
      readOnly: false
    }));
  };

  const getUniReasons = (): ICheckbox[] => {
    const uniReasons = [
      'University Reputation/Ranking',
      'Course Reputation/Ranking',
      'University Location',
      'Cost (e.g. affordabilty)',
      'Recommendation (e.g. Friend or family)',
      'University Facilities',
      'Other',
      'I am not sure why I chose this university'
    ];

    return uniReasons.map((reason: string) => ({
      id: reason,
      label: reason,
      isTicked: !!(selectedUniReasons && selectedUniReasons.includes(reason)),
      readOnly: false
    }));
  };

  const onCourseReasonsChange = (selectedReason: string, isTicked: boolean) => {
    let newSelectedCourseReasons = [];
    const currentCourseReasons = selectedCourseReasons.slice(0);

    if (!isTicked) {
      // remove from list
      newSelectedCourseReasons = currentCourseReasons.filter((reason: string) => {
        return reason !== selectedReason;
      });
    } else {
      // add to list
      currentCourseReasons.push(selectedReason);
      newSelectedCourseReasons = currentCourseReasons.slice(0);
    }
    onChange(Q.SELECTED_COURSE_REASONS, newSelectedCourseReasons);
  };

  const onUniReasonsChange = (selectedReason: string, isTicked: boolean) => {
    let newSelectedUniReasons = [];
    const currentUniReasons = selectedUniReasons.slice(0);

    if (!isTicked) {
      // remove from list
      newSelectedUniReasons = currentUniReasons.filter((reason: string) => {
        return reason !== selectedReason;
      });
    } else {
      // add to list
      currentUniReasons.push(selectedReason);
      newSelectedUniReasons = currentUniReasons.slice(0);
    }
    onChange(Q.SELECTED_UNI_REASONS, newSelectedUniReasons);
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={pageTitle}
    >
      <Helmet>
        <title>
          {`${pageTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <SelectWithOtherWithConfirm
        id={Q.WHY_UK}
        key={Q.WHY_UK}
        options={getWhyUkOptions()}
        label="Why did you choose to study in the UK?"
        inputHelperText={
          whyUk === 'Other' || whyUk === 'I am not sure why I chose to study in the UK'
            ? pageConfig.WhyUKOther.helpText
            : pageConfig.WhyUK.helpText
        }
        otherOptions={[
          {
            label: 'Other',
            value: 'Other'
          },
          {
            label: 'I am not sure why I chose to study in the UK',
            value: 'I am not sure why I chose to study in the UK'
          }
        ]}
        otherOptionLabel="Please provide more information"
        value={whyUk}
        onChange={(evt: any) => onChange(Q.WHY_UK, evt.currentTarget.value)}
        onChangeOtherText={(evt: any) => onChange(Q.WHY_UK_OTHER, evt.currentTarget.value)}
        otherValue={whyUkOther}
      />

      <CheckboxOptionsWithConfirm
        id="course-reasons"
        key={Q.SELECTED_COURSE_REASONS}
        inputHelperText={pageConfig.CourseReasons.helpText}
        checkboxes={getCourseReasons()}
        labelText="Why did you choose this course?"
        onChange={onCourseReasonsChange}
      />

      {selectedCourseReasons &&
        (selectedCourseReasons.findIndex(reason => reason === 'Other') > -1 ||
          selectedCourseReasons.findIndex(reason => reason === 'I am not sure why I chose this course') > -1) && (
          <InputWithConfirm
            id={Q.SELECTED_COURSE_REASONS_OTHER}
            key={Q.SELECTED_COURSE_REASONS_OTHER}
            value={selectedCourseReasonsOther}
            label={'Please provide more information'}
            inputHelperText={pageConfig.CourseReasonsOther.helpText}
            onChange={(evt: any) => onChange(Q.SELECTED_COURSE_REASONS_OTHER, evt.currentTarget.value)}
          />
        )}

      <CheckboxOptionsWithConfirm
        id="uni-reasons"
        key={Q.SELECTED_UNI_REASONS}
        inputHelperText={pageConfig.UniReasons.helpText}
        checkboxes={getUniReasons()}
        labelText="Why did you choose this university?"
        onChange={onUniReasonsChange}
      />

      {selectedUniReasons &&
        (selectedUniReasons.findIndex(reason => reason === 'Other') > -1 ||
          selectedUniReasons.findIndex(reason => reason === 'I am not sure why I chose this university') > -1) && (
          <InputWithConfirm
            id={Q.SELECTED_UNI_REASONS_OTHER}
            key={Q.SELECTED_UNI_REASONS_OTHER}
            value={selectedUniReasonsOther}
            label={'Please provide more information'}
            inputHelperText={pageConfig.UniReasonsOther.helpText}
            onChange={(evt: any) => onChange(Q.SELECTED_UNI_REASONS_OTHER, evt.currentTarget.value)}
          />
        )}

      <SubHeading>In your own words, please provide three reasons you chose this university?</SubHeading>

      <TextareaWithConfirm
        id="uni-choice-reason-1"
        key={Q.UNI_CHOICE_REASON_1}
        value={uniChoiceReason1}
        label="Reason 1"
        inputHelperText={pageConfig.UniChoiceReason1.helpText}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
          onChange(Q.UNI_CHOICE_REASON_1, event.currentTarget.value)
        }
      />

      <TextareaWithConfirm
        id="uni-choice-reason-2"
        key={Q.UNI_CHOICE_REASON_2}
        value={uniChoiceReason2}
        label="Reason 2"
        inputHelperText={pageConfig.UniChoiceReason2.helpText}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
          onChange(Q.UNI_CHOICE_REASON_2, event.currentTarget.value)
        }
      />

      <TextareaWithConfirm
        id="uni-choice-reason-3"
        key={Q.UNI_CHOICE_REASON_3}
        value={uniChoiceReason3}
        label="Reason 3"
        inputHelperText={pageConfig.UniChoiceReason3.helpText}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
          onChange(Q.UNI_CHOICE_REASON_3, event.currentTarget.value)
        }
      />
    </QuestionPage>
  );
};

export default InterviewQPage1;
